import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const articleSchema = {
  '@context': 'https://schema.org', '@type': 'Article',
  headline: 'Best Shoes Found on Vigorbuy Spreadsheet in 2026: Top Sneaker and Footwear Picks',
  description: 'Discover the top sneaker and shoe finds from the Vigorbuy Spreadsheet this year — from classic styles to trending releases across all price ranges.',
  author: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub' },
  datePublished: '2026-07-18', dateModified: '2026-07-22',
  publisher: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub', url: import.meta.env.VITE_SITE_URL },
  mainEntityOfPage: { '@type': 'WebPage', '@id': import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-best-shoes-2026' },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Best Shoes on Vigorbuy Spreadsheet 2026', item: import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-best-shoes-2026' },
  ],
};

export default function BestShoesPage() {
  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">Home</Link></li>
              <li><span className="text-foreground-300">/</span></li>
              <li><span className="text-foreground-700 font-medium">Best Shoes 2026</span></li>
            </ol>
          </nav>
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Product Spotlight</span>
              <time className="text-xs text-foreground-400">July 18, 2026</time>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              Best Shoes Found on Vigorbuy Spreadsheet in 2026
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              The Shoes category is consistently one of the most popular sections of the Vigorbuy Spreadsheet, and for
              good reason. With hundreds of verified sneaker and footwear listings, comprehensive QC photo documentation,
              and detailed community sizing feedback, it is the ultimate resource for shoe enthusiasts.
            </p>
          </header>
          <div className="prose-custom space-y-8">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Why the Shoes Category Stands Out</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                The Vigorbuy Spreadsheet Shoes category distinguishes itself through exceptional organization and depth.
                Unlike generic sneaker marketplaces where you must scroll endlessly to compare options, the spreadsheet
                presents footwear listings in a structured format that makes side-by-side comparison effortless. Each
                listing typically includes multiple QC angles — top-down, side profile, sole detail, and close-up
                logo shots — giving you a comprehensive view before committing to a purchase.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                Community engagement in the Shoes category is particularly strong. Experienced buyers regularly share
                detailed fit assessments, comparing spreadsheet finds against retail versions of the same models. This
                crowdsourced sizing data is invaluable, especially for international buyers who cannot try shoes on
                before purchasing. The community has essentially built a collaborative sizing database through years
                of shared experience.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Trending styles in 2026 span the full spectrum — from classic white leather sneakers and retro runners
                to chunky dad shoes and minimalist court styles. The spreadsheet reflects real-time market trends,
                with new releases appearing in the listings within days of hitting the broader market. For trend-conscious
                shoppers, this near-real-time curation is a significant advantage over slower-to-update alternatives.
              </p>
            </section>
            <div className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/70 text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">Discover Your Next Pair</h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">Explore the complete Shoes category on the Vigorbuy Spreadsheet now.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  Browse Shoes <i className="ri-arrow-right-line"></i>
                </a>
                <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-full border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap">
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}